import {fromSeconds} from 'common/date';
import {DateTime} from 'luxon';
import * as React from 'react';
import {
  formatCents,
  formatCentsShort,
  formatNetShort,
  inclusiveEndDay,
} from 'src/CardBenefitsView/benefitUtils';
import {buildOpeningSoon, nettedSpans} from 'src/CardBenefitsView/benefitWindows';
import type {FeeYearStats} from 'src/CardBenefitsView/benefitWindows';

type Tone = 'default' | 'sage' | 'blue' | 'amber' | 'red';

const TONE_CLASS: Record<Tone, string> = {
  default: '',
  sage: 'text-positive',
  blue: 'text-blue-600 dark:text-blue-300',
  amber: 'text-amber-600 dark:text-amber-400',
  red: 'text-destructive',
};

type StatTileProps = {
  value: React.ReactNode;
  tone?: Tone;
  sub: React.ReactNode;
};

export const StatTile = ({value, tone = 'default', sub}: StatTileProps) => (
  <div className="border-border bg-card rounded-lg border px-3.5 py-2.5">
    <div className={`text-base font-semibold tabular-nums ${TONE_CLASS[tone]}`}>
      {value}
    </div>
    <div className="text-muted-foreground text-[11px] leading-snug">{sub}</div>
  </div>
);

const formatDay = (seconds: number): string =>
  inclusiveEndDay(seconds).toLocaleString({month: 'short', day: 'numeric'});

/**
 * The current fee year in four numbers: where the year stands against the
 * fee, what is still claimable, what already slipped, and the best case left.
 */
export const ScorecardTiles = ({stats}: {stats: FeeYearStats}) => {
  const now = DateTime.now();

  // The open windows sharing the nearest deadline, for "$175 expires Sep 30"
  let expiringSum = 0;
  let nearestEnd: number | null = null;
  for (const summary of stats.benefits) {
    for (const window of summary.windows) {
      if (window.state !== 'open' || window.openRemaining <= 0) continue;
      if (nearestEnd == null || window.endDate < nearestEnd) {
        nearestEnd = window.endDate;
        expiringSum = window.openRemaining;
      } else if (window.endDate === nearestEnd) {
        expiringSum += window.openRemaining;
      }
    }
  }

  const openingSoon = buildOpeningSoon([stats.feePeriod], now, 60);
  // Sum EVERY future window inside the horizon, not buildOpeningSoon's
  // one-per-benefit list — a monthly credit can have two windows open
  // within 60 days
  const horizon = now.plus({days: 60});
  let openingSoonSum = 0;
  for (const summary of stats.benefits) {
    for (const window of summary.windows) {
      if (
        window.state === 'future' &&
        fromSeconds(window.startDate) <= horizon
      ) {
        openingSoonSum += Math.max(0, window.amount - window.usedAmount);
      }
    }
  }

  // Built from signed captured (not potential − missed) so a clawed-back
  // expired window keeps its negative balance and an over-cap window keeps
  // its overage in the best case
  const maxNet = stats.captured + stats.open + stats.future - stats.fee;

  return (
    <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
      <StatTile
        value={`net ${formatNetShort(stats.net)}`}
        tone={stats.net >= 0 ? 'sage' : 'red'}
        sub={`posted + booked − ${formatCentsShort(stats.fee)} fee`}
      />
      {stats.open > 0 ? (
        <StatTile
          value={`${formatCentsShort(stats.open)} claimable`}
          tone="blue"
          sub={
            nearestEnd != null
              ? `${stats.openWindowCount} open window${stats.openWindowCount === 1 ? '' : 's'} · ${formatCentsShort(expiringSum)} expires ${formatDay(nearestEnd)}`
              : `${stats.openWindowCount} open windows`
          }
        />
      ) : (
        <StatTile
          value="nothing open"
          sub={
            openingSoon.length > 0
              ? `next window opens ${fromSeconds(openingSoon[0].window.startDate).toLocaleString({month: 'short', day: 'numeric'})}`
              : 'no upcoming windows'
          }
        />
      )}
      <StatTile
        value={stats.missed > 0 ? `${formatCents(stats.missed)} missed` : 'nothing missed'}
        tone={stats.missed > 0 ? 'amber' : 'default'}
        sub={
          stats.missed > 0
            ? `${stats.missedWindowCount} window${stats.missedWindowCount === 1 ? '' : 's'} expired unused`
            : 'no windows expired unused yet'
        }
      />
      <StatTile
        value={`${formatNetShort(maxNet)} max`}
        sub={
          openingSoonSum > 0
            ? `year-end net if nothing lapses · ${formatCentsShort(openingSoonSum)} opens within 60d`
            : 'year-end net if nothing else lapses'
        }
      />
    </div>
  );
};

const CHART_HEIGHT = 128;

/**
 * Captured credits per fee year against that year's fee, bullet-style: one
 * quiet bar per year (booked portion hatched), the fee as a tick line across
 * the column. Bar past the tick = the fee paid for itself; the net label
 * makes the verdict explicit.
 */
export const YearBulletChart = ({years}: {years: FeeYearStats[]}) => {
  const ascending = [...years].sort(
    (a, b) => a.feePeriod.startDate - b.feePeriod.startDate,
  );
  const scaleMax =
    Math.max(
      1,
      ...ascending.map((year) => Math.max(year.captured, year.fee)),
    ) * 1.05;
  const px = (cents: number): number =>
    Math.round((Math.max(0, cents) / scaleMax) * CHART_HEIGHT);

  return (
    <div className="border-border bg-card rounded-lg border px-4 pt-3 pb-2.5">
      <div className="flex items-baseline justify-between">
        <span className="text-muted-foreground text-xs">
          Captured credits vs annual fee{' '}
          <span className="opacity-70">(points &amp; perks untracked)</span>
        </span>
        <span className="text-muted-foreground flex items-center gap-3 text-[10px]">
          <span className="flex items-center gap-1">
            <i className="inline-block h-2.5 w-2.5 rounded-[3px] bg-green-500/25 ring-1 ring-green-500/40 ring-inset" />
            captured
          </span>
          <span className="flex items-center gap-1">
            <i className="bg-foreground/50 inline-block h-[2px] w-3" />
            fee
          </span>
        </span>
      </div>
      <div
        className="mt-2 grid items-end gap-2"
        style={{
          gridTemplateColumns: `repeat(${ascending.length}, minmax(0, 1fr))`,
        }}
      >
        {ascending.map((year) => {
          const startYear = fromSeconds(year.feePeriod.startDate).year;
          const spans = nettedSpans(year);
          const posted = px(spans.posted);
          const booked = px(spans.posted + spans.booked) - posted;
          return (
            <div
              key={year.feePeriod._id}
              className="flex flex-col items-center gap-1"
              title={`FY ${startYear}: ${formatCents(Math.max(0, year.captured))} of ${formatCents(year.potential)} captured · ${formatCents(year.fee)} fee · net ${formatNetShort(year.net)}`}
            >
              <div className="relative w-full" style={{height: CHART_HEIGHT}}>
                <div className="absolute inset-x-0 bottom-0 mx-auto w-6">
                  {booked > 0 && (
                    <div
                      className="stripe-booked w-full rounded-t-[4px] ring-1 ring-green-500/30 ring-inset"
                      style={{height: booked}}
                    />
                  )}
                  <div
                    className={`w-full bg-green-500/25 ring-1 ring-green-500/40 ring-inset ${booked > 0 ? '' : 'rounded-t-[4px]'}`}
                    style={{height: Math.max(posted, spans.posted > 0 ? 2 : 0)}}
                  />
                </div>
                <div
                  className="bg-foreground/50 absolute left-1/2 h-[2px] w-9 max-w-full -translate-x-1/2"
                  style={{bottom: px(year.fee)}}
                />
              </div>
              <div
                className={`text-[10px] font-medium tabular-nums ${
                  year.net >= 0
                    ? 'text-positive'
                    : 'text-destructive'
                }`}
              >
                {/* Whole dollars — cents are noise at this size; the tooltip
                    and year rows carry exact values */}
                {formatNetShort(Math.round(year.net / 100) * 100)}
              </div>
              <div
                className={`text-[10px] tabular-nums ${
                  year.isCurrent
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground'
                }`}
              >
                &rsquo;{String(startYear).slice(2)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
